import { useEffect, useState } from 'react';
import moment from 'moment-timezone';

/**
 * The current time, re-read whenever a reminder display could change: at every minute
 * boundary while a reminder is upcoming (its countdown ticks), at its exact time (the countdown
 * gives way to the time), and at each midnight after (a time-only "4:30 PM" must gain its date
 * when the day turns). Nothing is scheduled without a reminder. Whether it rings is not the
 * clock's call — see useReminderRinging.
 */
const useReminderNow = (remindAt: moment.Moment | null): moment.Moment => {
	// Only a re-render trigger; the time itself is read fresh on every render so a reminder set
	// after mount never shows a countdown measured from a stale tick.
	const [tick, setTick] = useState(0);
	const remindAtMillis = remindAt ? remindAt.valueOf() : null;

	useEffect(() => {
		const currentMillis = Date.now();
		if (remindAtMillis === null) return undefined;
		const untilNextMinute = 60000 - (currentMillis % 60000);
		const untilMidnight = moment().add(1, 'day').startOf('day').valueOf() - currentMillis;
		const delay =
			remindAtMillis > currentMillis
				? Math.min(untilNextMinute, remindAtMillis - currentMillis)
				: untilMidnight;
		const timer = setTimeout(() => setTick((count) => count + 1), delay);
		return () => clearTimeout(timer);
	}, [remindAtMillis, tick]);

	return moment();
};

export default useReminderNow;
